import type { ReactNode } from 'react';
import type { To } from 'react-router';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { OverflowProps } from '../../data/Overflow/Overflow';
import type { GutterProp } from '../../utilities/shared/Gutter';
import type { BadgeProps } from '../Badge/Badge';
import { Icon } from '../../assets/Icon/Icon';
import { selectors } from '../../common/selectors';
import { transitions } from '../../common/transitions';
import { Overflow } from '../../data/Overflow/Overflow';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { Badge } from '../Badge/Badge';

const summaryTableValues = {
  row: { borderRadius: 10 },
  arrow: { size: 10 },
};

export interface SummaryTableCellProps {
  /**
   * The alignment of the cell content.
   */
  alignment?: 'start' | 'center' | 'end';
  /**
   * The badge to display in the cell.
   */
  badge?: Omit<BadgeProps, 'onClick' | 'size'>;
  /**
   * A unique identifier for the cell.
   */
  id: string;
  /**
   * The cell content.
   */
  cell: ReactNode;
  /**
   * The icon to display in the cell.
   */
  icon?: IconName;
  /**
   * Whether the cell is the leading cell, it will be bold and have a leading color.
   */
  isLeading?: boolean;
  /**
   * Whether to truncate the cell's content.
   */
  truncate?: boolean;
  /**
   * Whether to wrap the cell's content.
   */
  wrap?: boolean;
  /**
   * The width of the cell (e.g., '70%', '200px').
   */
  width?: string;
}

const SummaryTableCellIcon = styled(Icon, {
  variants: {
    isLeading: {
      true: {
        color: '$$summaryTableCellIconLeading',
      },
      false: {
        color: '$$summaryTableCellIcon',
      },
    },
  },
});
SummaryTableCellIcon.displayName = 'SummaryTableCellIcon';

const SummaryTableCellBadge = styled(Badge);
SummaryTableCellBadge.displayName = 'SummaryTableCellBadge';

const SummaryTableCellStack = styled(AlignStack);
SummaryTableCellStack.displayName = 'SummaryTableCellStack';

const SummaryTableCell = styled('div', Small, {
  display: 'table-cell',
  padding: '$6 $8',
  minWidth: 0,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',

  '&:first-child': {
    paddingLeft: 0,
  },

  '&:last-child': {
    paddingRight: 0,
  },

  variants: {
    alignment: {
      start: {},
      center: {
        '& > *': {
          margin: '0 auto',
        },
      },
      end: {
        '& > *': {
          margin: 'auto 0 auto auto',
        },
      },
    },
    dividers: {
      none: {},
      all: {
        strokeTopBottom: '$$summaryTabelCellStroke',
      },
      siblings: {
        strokeBottom: '$$summaryTabelCellStroke',
      },
    },
    isLeading: {
      true: {
        fontWeight: '$bold',
      },
    },
    isPressable: {
      true: {
        cursor: 'pointer',
      },
    },
    truncate: {
      true: {
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      false: {},
    },
    wrap: {
      true: {
        whiteSpace: 'normal',
      },
    },
    type: {
      arrow: {
        widthAll: `$${summaryTableValues.arrow.size}`,
        padding: 0,
      },
    },
  },

  compoundVariants: [
    {
      isLeading: true,
      isPressable: true,
      css: {
        color: '$$summaryTableCellLeading',
      },
    },
  ],

  defaultVariants: {
    truncate: true,
  },
});
SummaryTableCell.displayName = 'SummaryTableCell';

const SummaryTableArrowIcon = styled(Icon, {
  widthAll: `$${summaryTableValues.arrow.size}`,
  heightAll: `$${summaryTableValues.arrow.size}`,
});
SummaryTableArrowIcon.displayName = 'SummaryTableArrowIcon';

function SummaryTableArrow({ dividers }: { dividers: 'none' | 'all' | 'siblings' }) {
  return (
    <SummaryTableCell aria-hidden type="arrow" dividers={dividers} truncate="false">
      <SummaryTableArrowIcon icon="chevron-right" variant="neutral" />
    </SummaryTableCell>
  );
}

export interface SummaryTableRowProps {
  /**
   * A unique identifier for the row.
   */
  id: string;
  /**
   * The cells of the row.
   */
  cells: SummaryTableCellProps[];
  // Routing
  as?: React.ComponentType<any>;
  to?: To;
  onClick?: () => void;
}

const SummaryTableRow = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'table-row',
  $$summaryTabelCellStroke: colors.strokeNeutralLight,
  $$summaryTableCellIcon: colors.iconNeutralLight,
  $$summaryTableCellLeading: colors.linkAlternativeInitialLight,

  [darkThemeSelector]: {
    $$summaryTabelCellStroke: colors.strokeNeutralDark,
    $$summaryTableCellIcon: colors.iconNeutralDark,
    $$summaryTableCellLeading: colors.linkAlternativeInitialDark,
  },

  '&::before': {
    content: '',
    position: 'absolute',
    zIndex: -1,
    inset: '0 -$12',
    display: 'flex',
    borderRadius: `$${summaryTableValues.row.borderRadius}`,
    transition: transitions.focusRing,
  },

  variants: {
    dividers: {
      none: {},
      all: {},
      siblings: {},
    },
    hasOverflow: {
      true: {},
      false: {},
    },
    isPressable: {
      true: {
        cursor: 'pointer',

        [selectors.hover]: {
          zIndex: 2,
          background: colors.bgNeutralLight,
          $$summaryTabelCellStroke: colors.transparent,
          $$summaryTableCellIcon: colors.bodyNeutralLight,
          $$summaryTableCellLeading: colors.linkAlternativeInteractiveLight,
          $$summaryTableCellIconLeading: colors.linkAlternativeIconInteractiveLight,

          '&::before': {
            background: colors.bgNeutralLight,
            strokeAll: colors.strokeNeutralLight,
          },

          [darkThemeSelector]: {
            $$summaryTabelCellStroke: colors.transparent,
            $$summaryTableCellIcon: colors.bodyNeutralDark,
            $$summaryTableCellLeading: colors.linkAlternativeInteractiveDark,
            $$summaryTableCellIconLeading: colors.linkAlternativeIconInteractiveDark,

            '&::before': {
              background: colors.bgNeutralDark,
              strokeAll: colors.strokeNeutralDark,
            },
          },
        },

        [selectors.focus]: {
          zIndex: 3,
          outline: 'none',
          $$summaryTabelCellStroke: colors.transparent,
          $$summaryTableCellIcon: colors.bodyNeutralLight,
          $$summaryTableCellLeading: colors.linkAlternativeInteractiveLight,
          $$summaryTableCellIconLeading: colors.linkAlternativeIconInteractiveLight,

          [darkThemeSelector]: {
            $$summaryTabelCellStroke: colors.transparent,
            $$summaryTableCellIcon: colors.bodyNeutralDark,
            $$summaryTableCellLeading: colors.linkAlternativeInteractiveDark,
            $$summaryTableCellIconLeading: colors.linkAlternativeIconInteractiveDark,
          },

          '&::before': {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
    },
  },

  compoundVariants: [
    {
      dividers: 'siblings',
      hasOverflow: false,
      css: {
        '&:last-child': {
          $$summaryTabelCellStroke: colors.transparent,
        },
      },
    },
  ],
});
SummaryTableRow.displayName = 'SummaryTableRow';

const SummaryTableBody = styled('div', {
  display: 'table-row-group',
  width: '100%',
});
SummaryTableBody.displayName = 'SummaryTableBody';

const SummaryTableTable = styled('div', {
  display: 'table',
  width: '100%',

  variants: {
    truncate: {
      true: {
        tableLayout: 'fixed',
      },
      false: {
        tableLayout: 'auto',
      },
    },
  },
});
SummaryTableTable.displayName = 'SummaryTableTable';

const SummaryTableArea = styled('div', {
  display: 'flex',
  width: '100%',
});
SummaryTableArea.displayName = 'SummaryTableArea';

const SummaryTableContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '$4',
  width: '100%',
});
SummaryTableContainer.displayName = 'SummaryTableContainer';

export type SummaryTableProps = {
  /**
   * Whether to show dividers between the rows and how.
   */
  dividers?: 'none' | 'all' | 'siblings';
  /**
   * Handling the extra overflow of rows.
   */
  overflow?: {
    /**
     * The maximum number of rows to display in the summary table.
     */
    maximum?: number;
  } & OverflowProps;
  /**
   * The rows of the summary table.
   */
  rows: SummaryTableRowProps[];
  /**
   * Whether to truncate the cells.
   */
  truncate?: boolean;
} & GutterProp;

export function SummaryTable({
  gutter = { edge: 'all' },
  dividers = 'siblings',
  overflow,
  rows,
  truncate = true,
}: SummaryTableProps) {
  const css = gutterCSS({ edge: gutter.edge });
  const hasOverflow = overflow?.maximum && rows.length > overflow.maximum;

  return (
    <SummaryTableContainer css={css}>
      <SummaryTableArea>
        <SummaryTableTable truncate={truncate}>
          <SummaryTableBody>
            {rows.slice(0, overflow?.maximum).map((row) => {
              const isPressable = (row.as && row.to) || row.onClick;
              return (
                <SummaryTableRow
                  as={row.as}
                  to={row.to}
                  onClick={row.onClick}
                  tabIndex={isPressable ? 0 : undefined}
                  isPressable={!!isPressable}
                  dividers={dividers}
                  hasOverflow={!!hasOverflow}
                  key={row.id}
                >
                  {row.cells.map((cell) => {
                    const cellKey = `${row.id}-${cell.id}`;
                    const isLeading = Boolean(cell.isLeading);
                    const cellTruncate = cell.truncate ?? truncate;
                    return (
                      <SummaryTableCell
                        key={cellKey}
                        alignment={cell.alignment}
                        css={cell.width ? { width: cell.width } : undefined}
                        dividers={dividers}
                        isLeading={isLeading}
                        isPressable={!!isPressable}
                        truncate={cellTruncate}
                        wrap={cell.wrap}
                      >
                        <SummaryTableCellStack
                          align="center"
                          gap={6}
                          preset="small"
                          truncate={cellTruncate}
                          width={cell.width ? '100%' : 'fit-content'}
                          start={
                            cell.icon && (
                              <SummaryTableCellIcon
                                isLeading={isLeading}
                                icon={cell.icon}
                                size={14}
                              />
                            )
                          }
                          end={cell.badge && <SummaryTableCellBadge {...cell.badge} size="small" />}
                        >
                          {cell.cell}
                        </SummaryTableCellStack>
                      </SummaryTableCell>
                    );
                  })}
                  {isPressable && <SummaryTableArrow dividers={dividers} />}
                </SummaryTableRow>
              );
            })}
          </SummaryTableBody>
        </SummaryTableTable>
      </SummaryTableArea>
      {hasOverflow && (
        <Overflow
          as={overflow.as}
          to={overflow.to}
          onClick={overflow.onClick}
          label={overflow.label}
          labelOverride={overflow.labelOverride}
          total={rows.length}
        />
      )}
    </SummaryTableContainer>
  );
}
